import {
  IonMenu,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonList,
  IonMenuToggle,
  IonIcon,
  IonLabel,
  IonItem,
} from '@ionic/react';
import { createSelector } from '@reduxjs/toolkit';
import {
  arrowForward,
  bookOutline,
  calendarClearOutline,
  chevronForward,
  exitOutline,
  globe,
  shieldOutline,
} from 'ionicons/icons';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { t } from 'ttag';

import Platform from '@custom-types/Platform';
import Product from '@custom-types/Product';
import RoutePath from '@custom-types/RoutePath';
import { UserRole } from '@custom-types/UserSession';
import { logout as logoutFn } from '@pages/Auth/service';
import { LOCALE_TO_LABEL } from '@pages/Language';
import { generateRoute, getProduct } from '@services/navigation-service';
import { loggedOut, getLocale, getIsLoggedIn } from '@state/system';
import { getName, getRole } from '@state/user';
import { Browser } from '~capacitor-plugins/browser';
import { Capacitor } from '~capacitor-plugins/core';

const Description = styled.div`
  color: var(--ion-color-medium-tint);
`;

const List = styled(IonList)`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-bottom: var(--ion-safe-area-bottom);
`;

const Icon = styled(IonIcon)`
  width: 20px;
  height: 20px;
`;

const LogoutMenuToggle = styled(IonMenuToggle)`
  margin-top: auto;
`;

const selector = createSelector(
  getLocale,
  getRole,
  getName,
  getIsLoggedIn,
  (locale, role, name, isLoggedIn) => ({
    locale,
    role,
    name,
    isLoggedIn,
  })
);

const KnowledgeBaseLink = 'https://help.eventmobi.com/en/knowledge/using-the-lead-capture-app';

const DetailIcon = () => {
  const platform = Capacitor.getPlatform();
  if (platform === Platform.Android) {
    return <Icon slot="end" color="dark" icon={arrowForward} />;
  }
  return <Icon slot="end" color="dark" icon={chevronForward} />;
};

const Menu = () => {
  const dispatch = useDispatch();
  const { locale, role, name, isLoggedIn } = useSelector(selector);
  const product = getProduct();

  let showEventListItem = false;
  if (role) {
    showEventListItem = [UserRole.Organizer, UserRole.Staff, UserRole.LeadCaptureStaff].includes(
      role
    );
  }

  const logout = useCallback(async () => {
    await logoutFn();
    dispatch(loggedOut());
  }, [dispatch]);

  const language = LOCALE_TO_LABEL()[locale];

  return (
    <IonMenu contentId="main">
      <IonHeader>
        <IonToolbar>
          <IonTitle>
            {name ? (
              <>
                {t`Hello,`} {`${name}!`}
              </>
            ) : (
              t`Menu`
            )}
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <List>
          {showEventListItem && (
            <IonMenuToggle key="event" auto-hide="false">
              <IonItem
                button
                detail={false}
                routerLink={generateRoute(RoutePath.EventList)}
                routerDirection="back"
              >
                <IonIcon slot="start" color="dark" icon={calendarClearOutline} />
                <IonLabel>{t`Event List`}</IonLabel>
                <DetailIcon />
              </IonItem>
            </IonMenuToggle>
          )}
          <IonMenuToggle key="language" auto-hide="false">
            <IonItem button detail={false} color="" routerLink={generateRoute(RoutePath.Language)}>
              <IonIcon slot="start" color="dark" icon={globe} />
              <IonLabel>
                {t`Language`}
                <Description>{language}</Description>
              </IonLabel>
              <DetailIcon />
            </IonItem>
          </IonMenuToggle>

          <LogoutMenuToggle key="logout" auto-hide="false">
            {Product.CheckIn === product && role === UserRole.Staff && (
              <IonItem
                button
                lines="inset"
                detail={false}
                // skipcq: JS-0417
                routerLink={generateRoute(RoutePath.ManageMobi)}
              >
                <IonIcon slot="start" color="danger" icon={shieldOutline} />
                <IonLabel color="danger">ManageMobi</IonLabel>
              </IonItem>
            )}
            {isLoggedIn && (
              <>
                {Product.LeadCapture === product && (
                  <IonItem
                    button
                    lines="inset"
                    detail={false}
                    // skipcq: JS-0417
                    onClick={() => Browser.open({ url: KnowledgeBaseLink })}
                  >
                    <IonIcon slot="start" color="dark" icon={bookOutline} />
                    <IonLabel>{t`Knowledge Base`}</IonLabel>
                  </IonItem>
                )}
                <IonItem button lines="none" detail={false} onClick={logout}>
                  <IonIcon slot="start" color="dark" icon={exitOutline} />
                  <IonLabel>{t`Log Out`}</IonLabel>
                </IonItem>
              </>
            )}
          </LogoutMenuToggle>
        </List>
      </IonContent>
    </IonMenu>
  );
};

export default Menu;
